// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import PortfoliosService from "../../portfolios.service";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

const Pending: React.FC = (): ReactElement => {
  let searchTimer = null;
  const portfoliosService = new PortfoliosService();
  const navigate = useNavigate();
  const loadingState = useSelector((state: any) => state?.loading);
  const [pendingRequestsList, setPendingRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchPendingListData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.accountId}/${record?.account?.activeKycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_, record) => record?.account?.activeKyc?.nameEn,
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Portfolio Type",
      dataIndex: "portfolioType",
      key: "portfolioType",
      render: (_, record) => <span>{record?.portfolioType?.nameEn}</span>,
    },
    {
      title: "Request Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      sorter: true,
      render: (text) => (
        <span className=" text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <ReactSVG
          src={"/images/take-action.svg"}
          onClick={() => {
            navigate(`/portfolio/take-action/${record.id}`);
          }}
        />
      ),
    },
  ];

  const fetchPendingListData = async (page = 1): Promise<void> => {
    const data = await portfoliosService.getPortfoliosListData(
      ["Pending", "PENDING_DEPOSIT"],
      false,
      page,
      searchWord,
      sortField,
      sortOrder,
      filters
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setPendingRequestsList(data?.data?.portfolios);
    }
  };

  useEffect(() => {
    fetchPendingListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? pendingRequestsList?.length : ""} Pending requests
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {pendingRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={pendingRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchPendingListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Pending;
